import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import { formatMoney } from '@/lib/infra/formatters';
import { useAccountStore } from '../../stores/accountStore';
import { useMarketStore } from '../../stores/marketStore';
import { useDisplaySettings } from '../../stores/displaySettings';
import { bumpHeadroom, useHeadroom } from '../../hooks/useHeadroom';
import { describeLimitRejection, previewOrder, positionKey } from '../../lib/limits';
import { ExchangeOrderBook } from '@/components/organisms/ExchangeOrderBook';
import { useHydraOrderbookWarmup } from '@/hooks/useHydraOrderbookWarmup';
import { useFeeFor } from '@/services/hydraFees';
import { estimateAggregateFeeCents } from '@/lib/pricing/fees';
import { makerRebatePct } from '@/lib/pricing/makerRebate';
import { useMakerRebateActive } from '@/hooks/useMakerRebate';
import type { GameRow, TeamSide, MarketSide, BookLevel, VenueBook } from './types';
import { LiveBadge } from './GameTimeIndicators';
import { describeMarket } from '../../lib/kalshiTicker';
import { ConfirmModal } from '../../components/atoms/Modal';
import { americanOdds, bestPriceCentsForOrderSide, gameMarketKind, outcomeSideLabel } from './tradeModalBook';
import { resolvePolyOrderTokens } from './polyOrderTokens';
import {
  describePolyRelayUnavailable,
  placePolyOrderViaRelay,
} from '@/lib/polymarket/relay';
import { MiniLineStrip } from '../SpreadsTotals/MiniLineStrip';

// Same phantom-level filter the inline ladder + Yes/No pills use, hoisted
// here so the right-side deep orderbook shows the same sane view rather
// than diverging on tiny stub orders / cross-spread ghosts.
const LEVEL_SANITY_SLACK = 15;
function hasSize(l: BookLevel): boolean {
  return l.size >= 1;
}
function bidIsReal(lastTradeCents: number | null | undefined) {
  return (l: BookLevel): boolean => {
    if (!hasSize(l)) return false;
    if (lastTradeCents == null) return true;
    return l.priceCents <= lastTradeCents + LEVEL_SANITY_SLACK;
  };
}
/** Real best-bid price for the requested side (YES or NO). Reads the actual
 *  side ladder via bestPriceCentsForOrderSide rather than inverting YES — so
 *  this strip matches the orderbook the user is staring at, even when
 *  YES + NO don't sum to 100¢ across a wide spread. */
function formatSidePriceCents(book: VenueBook | undefined | null, side: MarketSide): string {
  const cents = bestPriceCentsForOrderSide(book ?? undefined, side);
  return cents == null ? '—' : `${cents}¢`;
}

/** Render Unabated consensus as a fair-probability percentage. UNLIKE
 *  K and P (which are order-book prices that flip semantics with the
 *  YES/NO toggle — NO is expensive for underdogs), U is a probability:
 *  the % chance this team wins, full stop. Same value whether the
 *  user is bidding YES or NO. Matches what the consensus chart shows. */
function formatConsensusCents(yesCents: number | null | undefined): string {
  if (yesCents == null || !Number.isFinite(yesCents)) return '—';
  return `${Math.round(yesCents)}%`;
}

function buildSide(
  book: VenueBook | null | undefined,
  marketSide: MarketSide,
): { bids: BookLevel[]; asks: BookLevel[] } {
  if (!book) return { bids: [], asks: [] };
  const ownBids = marketSide === 'yes' ? book.yes : book.no;
  const oppositeBids = marketSide === 'yes' ? book.no : book.yes;
  const lastYes = book.lastPriceCents;
  const ownLast = lastYes == null ? null : marketSide === 'yes' ? lastYes : 100 - lastYes;
  const oppositeLast = lastYes == null ? null : marketSide === 'yes' ? 100 - lastYes : lastYes;
  return {
    bids: ownBids.filter(bidIsReal(ownLast)),
    asks: oppositeBids
      .filter(bidIsReal(oppositeLast))
      .map((l) => ({ priceCents: 100 - l.priceCents, size: l.size })),
  };
}

/** The (team, venue, line-ticker) the order ticket's price input was last
 *  seeded from. Tracked across renders so {@link shouldReseedPriceOnBookChange}
 *  can distinguish a line swap from a team/venue switch. */
export interface ActiveBookSelection {
  ticker: string;
  side: TeamSide;
  venue: 'kalshi' | 'polymarket';
}

/**
 * Should an active-book change re-seed the price input with top-of-book?
 *
 * Re-seed ONLY when the *line ticker* changed while the (team, venue) held
 * steady — i.e. a spread/total chip swapped the line under the same
 * selection, so the old price belongs to a different bet and there's nothing
 * the trader explicitly picked to preserve.
 *
 * A ticker change that rides along with a team or venue switch is NOT a line
 * swap, and must NOT re-seed:
 *   - tab toggle (applyTeamToggle / applyVenueToggle already wrote the hint), or
 *   - a level click on the *other* team/venue's book — the butterfly ladder
 *     shows both teams + both venues at once — where the tradeIntent prefill
 *     just wrote the exact price the trader clicked.
 * Re-seeding there clobbers that selection with top-of-book. That was the
 * "first click into a ladder shows top-of-book, later clicks stick" bug: the
 * first click is the one that flips selectedSide/panelVenue, so it tripped
 * the reseed; subsequent clicks on the now-selected book didn't.
 *
 * `prev` is null on mount — initial seeding is the tradeIntent prefill's job.
 */
export function shouldReseedPriceOnBookChange(
  prev: ActiveBookSelection | null,
  next: { ticker: string | null; side: TeamSide; venue: 'kalshi' | 'polymarket' },
): boolean {
  if (prev === null) return false; // mount
  if (next.ticker === null) return false; // book dropped out
  if (prev.ticker === next.ticker) return false; // no line change
  return prev.side === next.side && prev.venue === next.venue;
}

export interface TradeIntent {
  side: 'away' | 'home';
  venue: 'kalshi' | 'polymarket';
  /** Pre-fill the order ticket's price input. Set when the click came
   *  from a specific price pill or orderbook level; omitted when the
   *  panel was opened by a row-expand toggle with no price context. */
  prefillPrice?: number;
  /** Pre-select YES/NO on the order ticket. Comes from the click target
   *  (e.g. a YES bid pill) rather than the panel's default. */
  initialOrderSide?: 'yes' | 'no';
  /** Maker-only when true (post-only mode). Threaded through from the
   *  click handler so the (deprecated) modal flow's postOnly intent
   *  still routes to the panel correctly. */
  postOnly?: boolean;
  /** The game/line key the `prefillPrice` was captured from. When set and it
   *  no longer matches the panel's current `game.key`, the prefill is treated
   *  as STALE and ignored, so the price seeds from the new book's best bid
   *  instead. Used by the value-dashboard spreads/totals tab, where selecting
   *  a different event swaps the panel's game without a fresh price click
   *  (unlike moneyline, whose row-expand resets the intent). Surfaces that
   *  leave this undefined (moneyline, GameDetail) keep the prior behavior. */
  gameKey?: string;
}

/** Spread/Total bet context. When provided, the panel hides its away/home
 *  team selector and per-team price strip, renders `title` in the header
 *  with the chip strip directly below it, and uses the `marketSide` YES/NO
 *  toggle as the only side control. The synthesized GameRow's away/home
 *  slots point to the same VenueBook, so the team selector is a no-op for
 *  these bets and the chip strip is the meaningful control. */
export interface BetLineContext {
  /** Side- and chip-aware bet description — e.g. YES "SF Giants wins by
   *  over 2.5 runs" vs NO "Oakland Athletics loses by under 2.5 runs";
   *  Total YES "SF @ LAD Total more than 8.5" vs NO "less than 8.5". The
   *  panel passes its own `panelSide` + `panelChipIdx` so the header
   *  tracks both the YES/NO toggle in OrderEntryForm AND any chip change
   *  inside the panel (which doesn't push back to the dashboard). */
  titleFor: (side: MarketSide, idx: number) => React.ReactNode;
  chips: { key: string; label: string; priceCents: number | null }[];
  /** Dashboard's currently-selected idx. The panel maintains its own
   *  local panelChipIdx initialized from this and synced when this changes
   *  (dashboard → panel). Panel-side chip clicks update local state only
   *  and never call back through `onSelectIdx`. */
  selectedIdx: number;
  /** Reserved for future "push from panel back to dashboard" flows; the
   *  current panel intentionally does NOT call this on chip changes so the
   *  dashboard stays put when the trader is exploring lines. */
  onSelectIdx: (idx: number) => void;
  /** Re-synthesize the GameRow for a different chip idx without touching
   *  the dashboard's selection. Returns null for an out-of-range idx. */
  getGameForIdx: (idx: number) => GameRow | null;
}

interface GameDetailPanelProps {
  game: GameRow;
  /** Close handler — omit when the panel is always-mounted (e.g.
   *  embedded in the GameDetail page's sticky right column). When
   *  absent, the X button isn't rendered. */
  onClose?: () => void;
  tradeIntent?: TradeIntent | null;
  marketSide: MarketSide;
  /** Keeps parent trade intent in sync when the user switches team tabs (row-click toggle uses this). */
  onTeamSideChange?: (side: TeamSide) => void;
  /** Set when the panel is rendering a synthesized spread/total bet — see
   *  BetLineContext. Drives the alternate header layout. */
  betLineContext?: BetLineContext | null;
  /** Unabated consensus prices (fair-prob cents, full-game) for the
   *  per-team summary block's "U" row. Omit on surfaces that don't
   *  subscribe to the SSE odds stream — the row is hidden when both
   *  values are missing. Cents-then-formatted by the panel so callers
   *  just thread the latest snapshot through.
   *
   *  Caller is responsible for aligning awayCents/homeCents to the
   *  panel's awayCode/homeCode columns BEFORE passing them in
   *  (compare unabated.awayTeam vs game.awayCode, swap if needed).
   *  K and P rows render straight off game.kalshi/polymarket.away+home
   *  — they're already self-consistent with the GameRow's column
   *  labels, so applying any cross-row swap to them would break the
   *  alignment that the moneyline value-dashboard view already shows
   *  correctly. */
  unabated?: {
    awayCents?: number | null;
    homeCents?: number | null;
  } | null;
}

// ── Order Entry Form ────────────────────────────────────────────

function OrderEntryForm({
  game,
  selectedSide,
  venue,
  orderSide,
  onOrderSideChange,
  price,
  onPriceChange,
  orderType,
  onOrderTypeChange,
  priceFlashKey,
}: {
  game: GameRow;
  selectedSide: TeamSide;
  /** Which venue this ticket targets. Drives both the book the form
   *  reads its ticker/tokens from AND which order-placement path
   *  handleSubmit dispatches to (accountStore.placeOrder for Kalshi,
   *  placePolyOrderViaRelay for Polymarket). */
  venue: 'kalshi' | 'polymarket';
  /** Controlled by GameDetailPanel — same value drives the orderbook ladders
   *  below, so toggling here ripples down but never up to the moneylines
   *  table (which holds its own independent marketSide). */
  orderSide: MarketSide;
  onOrderSideChange: (next: MarketSide) => void;
  /** Controlled price string — clicking a bid level in the orderbook
   *  below populates this; market orders ignore it. */
  price: string;
  onPriceChange: (next: string) => void;
  /** Increments on every external write to `price` (orderbook click,
   *  tradeIntent prefill, etc.) so the input can play a brief glow
   *  animation. User typing does NOT bump this. */
  priceFlashKey: number;
  /** 'limit' (post a resting bid at `price`) vs 'market' (take whatever's
   *  on the ask). Clicking an ask level flips this to 'market'. */
  orderType: 'limit' | 'market';
  onOrderTypeChange: (next: 'limit' | 'market') => void;
}) {
  const status = useAccountStore((s) => s.status);
  const placeOrder = useAccountStore((s) => s.placeOrder);
  const kalshiPositions = useAccountStore((s) => s.positions);
  const headroom = useHeadroom();
  const isKalshiVenue = venue === 'kalshi';

  // User-configurable defaults read from Settings → Preferences. Seed
  // once on mount; mid-session changes to these prefs are intentional
  // overrides for new tickets, not retroactive edits to an open one.
  // Qty starts BLANK at mount on purpose — pre-populating it once
  // caused phantom-size accidents when traders re-entered the panel.
  // Instead `defaultContracts` is used opportunistically on each
  // external price write (priceFlashKey bump) IF qty is still blank,
  // and via the explicit "+ N" button next to the Qty label.
  const defaultExpiryModePref = useDisplaySettings((s) => s.defaultExpiryMode);
  const defaultContractsPref = useDisplaySettings((s) => s.defaultContracts);
  // Maker-fee rebate fraction (0..1) for this ticket. 0 unless the org is
  // MM-enrolled and the MM Rebate toggle is on; live games rebate 100% of
  // the maker fee, pregame 50%. Only applied to maker (limit) orders below.
  const makerRebateActive = useMakerRebateActive();
  const makerRebateFraction = makerRebateActive
    ? makerRebatePct({ isLive: game.gameState === 'live' })
    : 0;

  const [qty, setQty] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);
  // Event-start availability needs to be known to decide whether the
  // user's GTS preference is honorable. Compute it before seeding
  // expiryMode so the lazy initializer can fall back to GTC when GTS
  // would be disabled.
  const gameStartMs = game.startTimeMs ?? null;
  const gtsAvailable = gameStartMs != null && gameStartMs > Date.now();

  // GTS auto-cancels at scheduled event start; GTC ("Good to Close")
  // rests until cancel / market settle; CUSTOM auto-cancels at a user-
  // picked datetime. Initial mode comes from the user's pref, EXCEPT
  // when the pref is GTS for a game that's already started (or has no
  // known event start) — in that case we degrade to GTC since the GTS
  // button would otherwise land selected-but-disabled. Market orders
  // fill immediately so the whole row is force-greyed out below.
  // Both venues now honor GTS / GTC / Custom — Polymarket via OrderType.GTD
  // wired through relay/src/polymarket/client.ts (was GTC-only when the
  // UI-lies bug was first papered over in deb0e85). Kalshi via
  // expirationTime as before.
  const [expiryMode, setExpiryMode] = useState<'gts' | 'gtc' | 'custom'>(
    () => {
      // Venue-agnostic: degrade pref → GTC when GTS is unavailable so the user
      // isn't stuck on a disabled mode.
      if (defaultExpiryModePref === 'gts' && !gtsAvailable) return 'gtc';
      return defaultExpiryModePref;
    },
  );
  // Auto-fall to GTC when GTS becomes disabled mid-session (game ticked past
  // start, or startTimeMs cleared). Keeps the trader from being stuck on a
  // disabled button they can't submit. Same rule for both venues now that
  // Polymarket honors GTD.
  useEffect(() => {
    if (expiryMode === 'gts' && !gtsAvailable) setExpiryMode('gtc');
  }, [expiryMode, gtsAvailable]);
  // Native datetime-local input value, e.g. "2026-05-23T19:30". Empty
  // until the user types one; submit-time guard rejects empty Custom.
  const [customExpiry, setCustomExpiry] = useState('');

  // Brief one-shot glow on the price input whenever something external
  // writes a new value (orderbook level click, prefill from tradeIntent).
  // `priceFlashKey` from the parent bumps on each external write; user
  // typing in the input doesn't go through that path so doesn't glow.
  //
  // Also opportunistically fills the Qty input with the user's default
  // contracts on each external price write — but ONLY when Qty is
  // still blank, so a trader who's already typed a size never gets it
  // stomped by a subsequent pill / orderbook tap.
  const [priceFlashing, setPriceFlashing] = useState(false);
  useEffect(() => {
    if (priceFlashKey === 0) return; // skip the mount-time tick
    setPriceFlashing(true);
    setQty((prev) => (prev === '' ? String(defaultContractsPref) : prev));
    // Two passes × 1.2s = 2.4s; small buffer so the class isn't yanked
    // before the CSS animation reaches its final frame.
    const t = setTimeout(() => setPriceFlashing(false), 2500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceFlashKey]);

  // Venue-aware book + ticker. The form reads from whichever venue's
  // book the panel is currently pointing at; switching the venue tab
  // above rebuilds this in lockstep via the prop change.
  const book = selectedSide === 'away'
    ? (isKalshiVenue ? game.kalshi?.away : game.polymarket?.away)
    : (isKalshiVenue ? game.kalshi?.home : game.polymarket?.home);
  const ticker = book?.marketTicker ?? '';

  // Poly outcome tokens — required to actually place a Poly order via
  // the relay. The book exposes its own YES token; the opposite side's
  // book carries the NO token (or a second moneyline ticker on this
  // book carries it for some shapes). resolvePolyOrderTokens handles
  // both layouts. Returns nulls when venue is Kalshi or no Poly book
  // is available; submit-time guards reject orders in those cases.
  const otherSideBook = !isKalshiVenue
    ? (selectedSide === 'away' ? game.polymarket?.home : game.polymarket?.away)
    : undefined;
  const polyTokens = !isKalshiVenue
    ? resolvePolyOrderTokens(book, otherSideBook)
    : { yes: null, no: null };
  const polyYesTokenId = polyTokens.yes;
  const polyNoTokenId = polyTokens.no;
  const polyEffectiveTokenId =
    !isKalshiVenue ? (orderSide === 'yes' ? polyYesTokenId : polyNoTokenId) : null;

  const bestYesHint = bestPriceCentsForOrderSide(book, 'yes');
  const bestNoHint = bestPriceCentsForOrderSide(book, 'no');

  const applyOrderSide = useCallback(
    (next: MarketSide) => {
      onOrderSideChange(next);
      setResult(null);
      const hint = bestPriceCentsForOrderSide(book, next);
      if (hint !== undefined) onPriceChange(String(hint));
    },
    [book, onOrderSideChange, onPriceChange],
  );

  // When the panel's side flips (main moneylines toggle pushed in, or the
  // user clicked our own BUY YES/NO above), refresh the price hint.
  useEffect(() => {
    setResult(null);
    const hint = bestPriceCentsForOrderSide(book, orderSide);
    if (hint !== undefined) onPriceChange(String(hint));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderSide]);

  useEffect(() => {
    const hint = bestPriceCentsForOrderSide(book, orderSide);
    if (hint === undefined) return;
    if (price === '') onPriceChange(String(hint));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book, orderSide]);

  const handleSubmit = useCallback(async () => {
    const priceCents = parseInt(price, 10);
    const count = parseInt(qty, 10);
    const isMarket = orderType === 'market';

    if (!ticker) {
      setResult({ ok: false, msg: `No ${isKalshiVenue ? 'Kalshi' : 'Polymarket'} market found` });
      return;
    }
    if (isNaN(count) || count < 1) { setResult({ ok: false, msg: 'Qty must be at least 1' }); return; }
    if (!isMarket && (isNaN(priceCents) || priceCents < 1 || priceCents > 99)) {
      setResult({ ok: false, msg: 'Price must be 1-99¢' });
      return;
    }

    // Poly-specific guards — token must resolve (some Poly markets
    // don't expose a NO outcome via hydra yet), and market orders
    // aren't supported through the relay yet.
    if (!isKalshiVenue && !polyEffectiveTokenId) {
      setResult({
        ok: false,
        msg: `No Polymarket outcome token available for ${orderSide.toUpperCase()} on this market.`,
      });
      return;
    }
    if (!isKalshiVenue && isMarket) {
      setResult({
        ok: false,
        msg: 'Polymarket market orders are not yet supported from this ticket.',
      });
      return;
    }

    // Resolve expiry as ms-since-epoch once, then derive the venue-specific
    // wire format below (Kalshi wants ISO 8601, Polymarket wants unix seconds).
    // Skipped for market orders since they fill immediately.
    let expirationMs: number | undefined;
    if (!isMarket) {
      if (expiryMode === 'gts' && gameStartMs != null) {
        expirationMs = gameStartMs;
      } else if (expiryMode === 'custom') {
        if (!customExpiry) {
          setResult({ ok: false, msg: 'Pick a custom expiry date/time first' });
          return;
        }
        // datetime-local is interpreted as local browser time; getTime()
        // returns UTC ms.
        const ms = new Date(customExpiry).getTime();
        if (!Number.isFinite(ms) || ms <= Date.now()) {
          setResult({ ok: false, msg: 'Custom expiry must be in the future' });
          return;
        }
        expirationMs = ms;
      }
    }
    const expirationTime = expirationMs != null ? new Date(expirationMs).toISOString() : undefined;
    const expirationTs = expirationMs != null ? Math.floor(expirationMs / 1000) : undefined;

    setSubmitting(true);
    setResult(null);
    try {
      if (isKalshiVenue) {
        await placeOrder({
          ticker,
          side: orderSide,
          action: 'buy',
          type: orderType,
          count,
          ...(isMarket ? {} : { price: priceCents }),
          ...(expirationTime ? { expirationTime } : {}),
        });
        const priceLabel = isMarket ? 'MKT' : `${priceCents}¢`;
        setResult({
          ok: true,
          msg: `Order placed: ${count} ${orderSide.toUpperCase()} @ ${priceLabel}`,
        });
      } else {
        const out = await placePolyOrderViaRelay({
          tokenId: polyEffectiveTokenId!,
          action: 'buy',
          priceCents,
          contracts: count,
          ...(book?.negRisk ? { negRisk: true } : {}),
          ...(book?.tickSize ? { tickSize: book.tickSize } : {}),
          ...(expirationTs ? { expirationTs } : {}),
        });
        if (!out.ok) {
          let msg: string;
          if (
            out.code === 'LIMIT_MAX_CONTRACTS' ||
            out.code === 'LIMIT_MAX_POSITIONS' ||
            out.code === 'LIMIT_SPORT_NOT_ALLOWED' ||
            out.code === 'LIMIT_NON_SPORT_DISABLED' ||
            out.code === 'LIMIT_POLYMARKET_DISABLED' ||
            out.code === 'LIMIT_MARKET_SPORT_UNKNOWN'
          ) {
            msg = describeLimitRejection(out.code, out.error);
          } else if (out.code === 'POLYMARKET_GEOBLOCK') {
            msg = out.error;
          } else if (out.code) {
            msg = describePolyRelayUnavailable(out.code);
          } else {
            msg = out.error;
          }
          setResult({ ok: false, msg });
          return;
        }
        setResult({ ok: true, msg: `Polymarket order placed: ${count} ${orderSide.toUpperCase()} @ ${priceCents}¢` });
      }
      setQty('');
    } catch (err) {
      setResult({ ok: false, msg: err instanceof Error ? err.message : 'Order failed' });
    } finally {
      setSubmitting(false);
    }
  }, [
    ticker,
    orderSide,
    orderType,
    price,
    qty,
    placeOrder,
    expiryMode,
    gameStartMs,
    customExpiry,
    isKalshiVenue,
    polyEffectiveTokenId,
    book?.negRisk,
    book?.tickSize,
  ]);

  // Hydra-sourced per-series fee schedule. `feeEntry` is undefined while the
  // initial fetch is in flight (or for a series the schedule hasn't seen yet);
  // when it is, we render "—" instead of guessing a rate. Called above the
  // early returns below so hook order stays stable when `ticker` flips from
  // empty (no Kalshi listing yet, e.g. WNBA Poly-only games) to populated.
  const feeEntry = useFeeFor(ticker);

  // ── Pre-trade limit preview (advisory; relay is the authoritative gate)
  // For Kalshi, we know the (ticker, side) keys from the local accountStore
  // and can run previewOrder against them. Keep this hook above the early
  // returns so the ticket remains stable while venue/ticker availability flips.
  const openPositionKeys = useMemo<Set<string>>(() => {
    const keys = new Set<string>();
    for (const p of kalshiPositions ?? []) {
      if (p.position > 0) keys.add(positionKey(p.ticker, p.side));
    }
    return keys;
  }, [kalshiPositions]);

  // Kalshi orders need an active Kalshi connection; Poly orders flow
  // through the relay which has its own connect status (described in
  // the error message if it's down). Only gate on the Kalshi-connected
  // status when this ticket is targeting Kalshi.
  if (isKalshiVenue && status !== 'connected') {
    return (
      <div className="border-border border-b px-4 py-3">
        <p className="text-muted-foreground text-xs">Connect your Kalshi account in Settings to trade</p>
      </div>
    );
  }

  if (!ticker) {
    return (
      <div className="border-border border-b px-4 py-3">
        <p className="text-muted-foreground text-xs">
          No {isKalshiVenue ? 'Kalshi' : 'Polymarket'} market available for this team.
        </p>
      </div>
    );
  }

  if (!isKalshiVenue && !polyYesTokenId && !polyNoTokenId) {
    return (
      <div className="border-border border-b px-4 py-3">
        <p className="text-muted-foreground text-xs">
          This Polymarket cell has no outcome tokens attached yet — open orders + cancellation
          still work from Settings / Positions, but placement isn't wired here.
        </p>
      </div>
    );
  }

  const priceCents = parseInt(price, 10) || 0;
  const count = parseInt(qty, 10) || 0;
  const cost = (priceCents * count) / 100;

  const previewBreach =
    headroom && count > 0 && ticker
      ? previewOrder(
          { maxContracts: headroom.maxContracts, maxPositions: headroom.maxPositions },
          {
            openContracts: headroom.openContracts,
            openPositions: headroom.openPositions,
            intentContracts: headroom.intentContracts,
            openPositionKeys,
          },
          {
            action: 'buy',
            ticker: isKalshiVenue ? ticker : (polyEffectiveTokenId ?? ticker),
            side: orderSide,
            count,
          },
        ).breach
      : null;
  // Polymarket can't reliably check position-key novelty at the client
  // either (tokenId vs conditionId), so suppress that-specific advisory
  // breach for poly. The relay enforces contracts cap on poly though,
  // which is the more important guardrail.
  const effectiveBreach =
    previewBreach && !isKalshiVenue && previewBreach.rule === 'LIMIT_MAX_POSITIONS'
      ? null
      : previewBreach;
  const submitDisabled =
    submitting || !qty || (orderType === 'limit' && !price) || !!effectiveBreach;
  // Limit orders rest = maker; market orders cross = taker. This is a
  // pre-fill *estimate*: a limit that never fills costs nothing, and a
  // marketable limit can be matched as a taker. See PROJECT-BIBLE.md.
  const isMaker = orderType === 'limit';
  const feeRate = !feeEntry
    ? null
    : feeEntry.feeType === 'flat'
      ? null
      : isMaker
        ? (feeEntry.feeMakerRate ?? 0)
        : (feeEntry.feeTakerRate ?? 0);
  const singleFillCents =
    feeRate === null || count === 0
      ? null
      : estimateAggregateFeeCents({ priceCents, count, feeRate });
  // MM Program rebate: a fraction of the maker fee (50% pregame / 100%
  // live), applied only to maker (limit) orders. Reduces the displayed fee;
  // the rebate itself settles off-API with Kalshi. 0 when not active.
  const makerRebateCents =
    singleFillCents === null || !isMaker || makerRebateFraction <= 0
      ? 0
      : singleFillCents * Math.min(makerRebateFraction, 1);
  // No site-calibration on the pre-trade ticket: the aggregate per-fill ceil
  // already equals Kalshi's own ticket figure (it bakes in their 1¢ minimum),
  // and this preview must match what Kalshi advertises side-by-side. The site
  // coefficient is a historical multi-fill inflation factor (taker orders that
  // split into many 1¢-floored fills) — applying it here over-stated the fee
  // (e.g. $93.71 vs Kalshi's $85.26). It still drives the MyTrades realized-
  // fee reconciliation, where multi-fill reality is the right model.
  const netFeeCents = singleFillCents === null ? null : singleFillCents - makerRebateCents;
  const netFee = netFeeCents === null ? null : netFeeCents / 100;
  const feeLabel =
    feeRate === null
      ? '—'
      : makerRebateCents > 0
        ? `${formatMoney(netFee ?? 0)} MMR`
        : formatMoney(netFee ?? 0);
  // Total dollars out the door = contracts × price + total fee. This is
  // what the trader actually spends — drives both the Cost line and the
  // post-fee American-odds calc (payout $1 / cost-per-contract).
  const totalCost = netFee === null ? null : cost + netFee;
  const totalCostLabel = totalCost === null ? '—' : formatMoney(totalCost);
  // Each contract pays $1 on win — gross payout, not net of fee.
  const maxPayout = count;
  const maxPayoutLabel = formatMoney(maxPayout);
  // American odds priced off the all-in cost per contract: $1 payout
  // divided by (price + fee-per-contract). Matches what a sportsbook
  // would advertise for the same effective fill.
  const allInPriceCents =
    totalCost === null || count === 0 ? null : (totalCost * 100) / count;
  const effectiveOddsLabel =
    allInPriceCents === null ? '—' : americanOdds(allInPriceCents);
  // Max Profit = Max Payout − Total Cost (gross $1×qty win minus what
  // you actually paid). Null when fee isn't loaded so we don't fake a
  // number that ignores the fee.
  const maxProfit = totalCost === null ? null : maxPayout - totalCost;
  const maxProfitLabel =
    maxProfit === null ? '—' : formatMoney(maxProfit, { showSign: true });

  return (
    <div className="border-border space-y-3 border-b px-4 py-3">
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground text-xs font-medium">Order Entry</span>
        <span className="text-muted-foreground/50 font-mono text-[10px]">{ticker.length > 30 ? `...${ticker.slice(-28)}` : ticker}</span>
      </div>

      {/* Side toggle */}
      <div className="space-y-1.5">
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => applyOrderSide('yes')}
            className={cn(
              'flex-1 rounded-md py-1.5 text-xs font-semibold transition-colors',
              orderSide === 'yes'
                ? 'bg-positive text-white'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            )}
          >
            BUY YES
          </button>
          <button
            type="button"
            onClick={() => applyOrderSide('no')}
            className={cn(
              'flex-1 rounded-md py-1.5 text-xs font-semibold transition-colors',
              orderSide === 'no'
                ? 'bg-negative text-white'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            )}
          >
            BUY NO
          </button>
        </div>
        <p className="text-muted-foreground text-center text-[11px]">
          {outcomeSideLabel(game, selectedSide, orderSide)}
        </p>
      </div>

      {/* Limit / Market toggle — clicking a bid level in the orderbook below
          flips this to LIMIT, clicking an ask flips it to MARKET. User can
          also toggle manually. */}
      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={() => { onOrderTypeChange('limit'); setResult(null); }}
          className={cn(
            'flex-1 rounded-md py-1 text-[11px] font-semibold transition-colors',
            orderType === 'limit'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:text-foreground',
          )}
        >
          LIMIT
        </button>
        <button
          type="button"
          onClick={() => { onOrderTypeChange('market'); setResult(null); }}
          className={cn(
            'flex-1 rounded-md py-1 text-[11px] font-semibold transition-colors',
            orderType === 'market'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:text-foreground',
          )}
        >
          MARKET
        </button>
      </div>

      {/* Good to Start / Good to Close / Custom — sits directly under
          LIMIT/MARKET per template. Whole row greys out for MARKET
          orders since they fill immediately and expirationTime is
          meaningless. GTS additionally greys out when the game has no
          known startTimeMs or has already started. CUSTOM reveals a
          datetime-local input below the row. */}
      <div className="space-y-1.5">
        <div className="flex gap-1.5">
          {(['gts', 'gtc', 'custom'] as const).map((mode) => {
            const isMarket = orderType === 'market';
            // Both venues honor expiry: Kalshi via expirationTime (ISO),
            // Polymarket via OrderType.GTD + UserOrderV2.expiration (unix
            // seconds). GTS button still greys out when the game has no
            // scheduled start or has already started — that's a data
            // condition, not a venue capability.
            const disabled =
              isMarket || (mode === 'gts' && !gtsAvailable);
            return (
              <button
                key={mode}
                type="button"
                onClick={() => { if (!disabled) { setExpiryMode(mode); setResult(null); } }}
                disabled={disabled}
                title={
                  isMarket
                    ? 'Market orders fill immediately — expiry n/a'
                    : mode === 'gts'
                      ? gtsAvailable
                        ? 'Good to Start — auto-cancels at scheduled event start if unfilled'
                        : 'Good to Start unavailable: game has no scheduled start or already started'
                      : mode === 'gtc'
                        ? 'Good to Close — rests until you cancel or the market settles'
                        : 'Custom — pick a specific date/time for auto-cancel'
                }
                className={cn(
                  'flex-1 rounded-md py-1 text-[11px] font-semibold transition-colors',
                  expiryMode === mode && !isMarket
                    ? 'bg-primary text-primary-foreground'
                    : disabled
                      ? 'bg-muted/40 text-muted-foreground/40 cursor-not-allowed'
                      : 'bg-muted text-muted-foreground hover:text-foreground',
                )}
              >
                {mode === 'gts' ? 'GOOD TO START' : mode === 'gtc' ? 'GOOD TO CLOSE' : 'CUSTOM'}
              </button>
            );
          })}
        </div>
        {orderType === 'limit' && expiryMode === 'custom' && (
          <input
            type="datetime-local"
            value={customExpiry}
            onChange={(e) => { setCustomExpiry(e.target.value); setResult(null); }}
            className="border-border bg-input text-foreground focus:border-primary w-full rounded-md border px-2.5 py-1.5 font-mono text-[11px] focus:outline-none"
          />
        )}
      </div>

      {/* Price + Qty */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-muted-foreground mb-1 block text-[10px]">
            Price (¢){orderType === 'market' ? ' — n/a' : ''}
          </label>
          <input
            type="number"
            min={1}
            max={99}
            disabled={orderType === 'market'}
            value={orderType === 'market' ? '' : price}
            onChange={(e) => { onPriceChange(e.target.value); setResult(null); }}
            placeholder={
              orderType === 'market'
                ? 'best ask'
                : (orderSide === 'yes' ? bestYesHint : bestNoHint) !== undefined
                  ? String(orderSide === 'yes' ? bestYesHint : bestNoHint)
                  : '50'
            }
            className={cn(
              'border-border bg-input text-foreground placeholder-muted-foreground focus:border-primary w-full rounded-md border px-2.5 py-1.5 font-mono text-xs focus:outline-none disabled:opacity-50',
              priceFlashing && 'animate-price-flash',
            )}
          />
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-muted-foreground text-[10px]">Qty</label>
            {/* Explicit one-tap "apply user default" — overrides whatever
                is in Qty regardless of current value. Distinct from the
                auto-fill on external price writes (which only fires
                when Qty is blank). */}
            <button
              type="button"
              onClick={() => { setQty(String(defaultContractsPref)); setResult(null); }}
              title={`Set Qty to your default (${defaultContractsPref}) — change in Settings → Preferences`}
              className="text-muted-foreground hover:text-foreground text-[10px] font-medium leading-none"
            >
              + {defaultContractsPref}
            </button>
          </div>
          <input
            type="number"
            min={1}
            value={qty}
            onChange={(e) => { setQty(e.target.value); setResult(null); }}
            placeholder="0"
            className="border-border bg-input text-foreground placeholder-muted-foreground focus:border-primary w-full rounded-md border px-2.5 py-1.5 font-mono text-xs focus:outline-none"
          />
        </div>
      </div>

      {/* Limit-breach preview (advisory; the relay is the authoritative gate)
          When effectiveBreach is non-null, the order would exceed one of the
          trader's caps. We disable the submit button below as the soft gate
          + structured error rendering covers the case where the relay
          rejects despite stale local state. */}
      {effectiveBreach && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 px-2.5 py-1.5 text-[11px] text-red-400">
          {effectiveBreach.rule === 'LIMIT_MAX_CONTRACTS'
            ? `Would exceed your contract cap: ${effectiveBreach.current.toLocaleString()} + ${effectiveBreach.requested.toLocaleString()} > ${effectiveBreach.cap.toLocaleString()}`
            : `Would exceed your position cap: ${effectiveBreach.current.toLocaleString()} + 1 > ${effectiveBreach.cap.toLocaleString()}`}
        </div>
      )}

      {/* Submit — sits directly under the price/qty inputs so the primary
          action is always reachable without scrolling past the summary. */}
      <button
        onClick={() => { handleSubmit().then(() => bumpHeadroom()); }}
        disabled={submitDisabled}
        className="bg-primary text-primary-foreground hover:bg-primary/90 flex w-full items-center justify-center gap-2 rounded-md py-2 text-xs font-semibold transition-colors disabled:opacity-50"
      >
        {submitting && <Loader2 size={12} className="animate-spin" />}
        {submitting ? 'Placing...' : 'Place Order'}
      </button>

      {/* Price / Total Cost (fee) / Max Payout / Max Profit — fee is the
          per-series Kalshi rate from hydra (taker on market, maker on
          limit). Max Payout is gross qty × $1; Max Profit is net of fee.
          Maker fees only apply on fill — a resting limit that never fills
          costs nothing. Same four-field shape as TradeModal + the SELL
          modal so the trader sees the same layout everywhere. Outlined
          card matches the framing the GameDetail TradeTicketPanel used
          to give this summary block a visual home. */}
      {count > 0 && priceCents > 0 && (
        <div className="border-border space-y-1 rounded-lg border px-3 py-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Price</span>
            <span className="text-foreground font-mono inline-flex items-center gap-1.5">
              <span>{priceCents}¢</span>
              <span className="h-3 w-px self-center bg-current opacity-30" />
              <span className="opacity-70">{americanOdds(priceCents)}</span>
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span
              className="text-muted-foreground"
              title={
                isMaker
                  ? 'Maker fee — applied only when the resting order fills'
                  : 'Taker fee — applied immediately on fill'
              }
            >
              Total Cost (fee)
            </span>
            <span className="text-foreground font-mono">
              {totalCostLabel}{' '}
              <span className="text-muted-foreground">({feeLabel})</span>
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground" title="Gross payout per contract on win — $1 each">
              Max Payout
            </span>
            <span className="text-positive font-mono inline-flex items-center gap-1.5">
              <span>{maxPayoutLabel}</span>
              <span className="h-3 w-px self-center bg-current opacity-30" />
              <span className="opacity-70">{effectiveOddsLabel}</span>
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground" title="Max Payout − Total Cost (net of fee)">
              Max Profit
            </span>
            <span
              className={`font-mono ${
                maxProfit === null
                  ? 'text-muted-foreground'
                  : maxProfit >= 0
                    ? 'text-positive'
                    : 'text-negative'
              }`}
            >
              {maxProfitLabel}
            </span>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className={cn(
          'rounded-md px-2.5 py-1.5 text-xs',
          result.ok ? 'bg-positive/10 text-positive' : 'bg-negative/10 text-negative'
        )}>
          {result.msg}
        </div>
      )}
    </div>
  );
}

// ── Game Detail Panel ───────────────────────────────────────────

/**
 * Open Kalshi orders for THIS game, with inline cancel — so a trader can kill a
 * specific resting order straight from the game card instead of hunting for it in
 * the positions tab (StreamRift trading-safety asks #2 + #3). Matches orders to the
 * game by the moneyline away/home market tickers. Renders nothing when there are none.
 */
function GameOpenOrders({ game }: { game: GameRow }) {
  const openOrders = useAccountStore((s) => s.openOrders);
  const cancelOrder = useAccountStore((s) => s.cancelOrder);
  // Honor the app-wide confirm-before-cancel setting (default ON) like every other
  // cancel surface — an inline card cancel must not bypass the trader's safety gate.
  const confirmOrderCancel = useDisplaySettings((s) => s.confirmOrderCancel);
  const [cancelingId, setCancelingId] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [pendingCancel, setPendingCancel] = useState<{ orderId: string; label: string } | null>(null);
  const awayTicker = game.kalshi?.away?.marketTicker;
  const homeTicker = game.kalshi?.home?.marketTicker;
  const gameTickers = useMemo(
    () => new Set([awayTicker, homeTicker].filter((t): t is string => !!t)),
    [awayTicker, homeTicker],
  );
  const orders = openOrders.filter((o) => gameTickers.has(o.ticker));
  if (orders.length === 0) return null;

  const executeCancel = async (orderId: string) => {
    setCancelError(null);
    setCancelingId(orderId);
    try {
      await cancelOrder(orderId);
    } catch (err) {
      // A failed cancel must NOT look like success on a live-game card — surface it.
      setCancelError(
        err instanceof Error && err.message ? err.message : 'Cancel failed. Check open orders and try again.',
      );
    } finally {
      setCancelingId(null);
    }
  };
  const requestCancel = (orderId: string, label: string) => {
    setCancelError(null);
    if (confirmOrderCancel) {
      setPendingCancel({ orderId, label });
      return;
    }
    void executeCancel(orderId);
  };

  return (
    <div className="border-border mt-3 rounded-lg border px-3 py-2.5">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-muted-foreground text-xs font-medium">Open orders — this game</span>
        <span className="text-muted-foreground/60 text-[10px]">cancel without leaving the card</span>
      </div>
      <div className="space-y-1">
        {orders.map((ord) => {
          const { marketLabel, betTeam } = describeMarket(ord.ticker, ord.side);
          const label = betTeam ? `${marketLabel} (${betTeam})` : marketLabel;
          // Kalshi often returns only yes_price; a NO order's real price is 100 − yes_price.
          const sidePrice =
            ord.side === 'yes'
              ? ord.yesPrice ?? (ord.noPrice != null ? 100 - ord.noPrice : null)
              : ord.noPrice ?? (ord.yesPrice != null ? 100 - ord.yesPrice : null);
          return (
            <div
              key={ord.orderId}
              className="hover:bg-foreground/5 flex items-center justify-between gap-2 rounded px-1.5 py-1 text-xs"
            >
              <div className="flex items-center gap-1.5 overflow-hidden">
                <span
                  className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-bold ${
                    ord.side === 'yes' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                  }`}
                >
                  {betTeam ? `(${betTeam}) ` : ''}
                  {ord.side === 'yes' ? 'YES' : 'NO'}
                </span>
                <span className="text-muted-foreground">{ord.action}</span>
                <span className="text-foreground font-mono">
                  {ord.remainingCount} @ {sidePrice != null ? `${sidePrice}¢` : '—'}
                </span>
              </div>
              <button
                onClick={() => requestCancel(ord.orderId, label)}
                disabled={cancelingId === ord.orderId}
                className="shrink-0 rounded border border-red-500/40 px-2 py-0.5 text-[10px] font-medium text-red-400 hover:bg-red-500/10 disabled:opacity-50"
              >
                {cancelingId === ord.orderId ? 'Canceling…' : 'Cancel'}
              </button>
            </div>
          );
        })}
      </div>
      {cancelError && <div className="mt-1.5 text-[10px] text-red-400">{cancelError}</div>}
      <ConfirmModal
        isOpen={pendingCancel !== null}
        onClose={() => setPendingCancel(null)}
        onConfirm={() => {
          if (pendingCancel) void executeCancel(pendingCancel.orderId);
        }}
        title="Cancel order?"
        message={pendingCancel ? `Cancel ${pendingCancel.label}?` : 'Cancel this order?'}
        confirmLabel="Cancel order"
        cancelLabel="Back"
        variant="danger"
      />
    </div>
  );
}

export function GameDetailPanel({ game: gameProp, onClose, tradeIntent, marketSide, onTeamSideChange, betLineContext = null, unabated = null }: GameDetailPanelProps) {
  const [selectedSide, setSelectedSide] = useState<TeamSide>(tradeIntent?.side ?? 'away');
  // Panel-local venue (Kalshi vs Polymarket). Seeded from tradeIntent so
  // a Poly pill click on the dashboard opens the ticket on the Poly side.
  // Same primitive-dep pattern as selectedSide so the parent's tradeIntent
  // object-literal churn doesn't stomp local toggles.
  const [panelVenue, setPanelVenue] = useState<'kalshi' | 'polymarket'>(
    tradeIntent?.venue ?? 'kalshi',
  );

  // Panel-local YES/NO state. Initialized from `marketSide` and re-synced
  // whenever the parent changes it (so toggling YES/NO on the moneylines
  // table still cascades into the panel + its ladders). The order-entry
  // form's BUY YES/BUY NO toggle drives this back through onOrderSideChange,
  // which only updates this local state — the parent's marketSide is never
  // pushed up, so the moneylines table stays put.
  const [panelSide, setPanelSide] = useState<MarketSide>(marketSide);
  useEffect(() => {
    setPanelSide(marketSide);
  }, [marketSide]);

  // Panel-side chip "override" — set when the user clicks a chip inside
  // the panel. Tagged with the dashboard's selectedIdx at the moment of
  // the click; if the dashboard later moves, that tag stops matching and
  // we fall back to the dashboard's idx *synchronously* on the same
  // render (no useEffect lag). Same one-way pattern as panelSide above:
  // dashboard → panel sync, but panel clicks don't push back.
  const [panelChipOverride, setPanelChipOverride] = useState<{
    value: number;
    basedOnDashboardIdx: number;
  } | null>(null);

  const panelChipIdx =
    betLineContext &&
    panelChipOverride &&
    panelChipOverride.basedOnDashboardIdx === betLineContext.selectedIdx
      ? panelChipOverride.value
      : (betLineContext?.selectedIdx ?? 0);

  const handlePanelChipSelect = useCallback(
    (idx: number) => {
      if (!betLineContext) return;
      setPanelChipOverride({ value: idx, basedOnDashboardIdx: betLineContext.selectedIdx });
    },
    [betLineContext],
  );

  // Re-synthesize a divergent GameRow when the panel's effective idx
  // differs from the dashboard's (i.e. user clicked inside the panel).
  // When they match, falls through to the prop.
  const game: GameRow = (() => {
    if (!betLineContext) return gameProp;
    if (panelChipIdx === betLineContext.selectedIdx) return gameProp;
    return betLineContext.getGameForIdx(panelChipIdx) ?? gameProp;
  })();

  // A prefill price goes STALE once the panel's game no longer matches the
  // game the click came from — e.g. the spreads/totals tab swapped the active
  // event without a new price click. When tradeIntent tags a gameKey that
  // doesn't match, ignore the prefill so the price seeds from the new book's
  // best bid. Untagged intents (moneyline, GameDetail) always "match".
  const prefillMatchesGame =
    tradeIntent?.gameKey == null || tradeIntent.gameKey === game.key;

  // Form intent state lifted up so orderbook-row clicks can populate the
  // OrderEntryForm: clicking a bid prefills the price + flips type=limit;
  // clicking an ask flips type=market (price ignored).
  const [formPrice, setFormPrice] = useState(
    () =>
      tradeIntent?.prefillPrice != null && prefillMatchesGame
        ? String(tradeIntent.prefillPrice)
        : '',
  );
  const [formOrderType, setFormOrderType] = useState<'limit' | 'market'>('limit');
  // Bumps on every external write to formPrice. OrderEntryForm watches
  // it to play a one-shot glow on the input. User typing in the input
  // goes through onPriceChange → setFormPrice directly and does NOT
  // bump, so typing never triggers the animation.
  const [priceFlashKey, setPriceFlashKey] = useState(0);
  const writeFormPriceFromExternal = useCallback((next: string) => {
    setFormPrice(next);
    setPriceFlashKey((k) => k + 1);
  }, []);

  // Pull a fresh price hint from the newly-selected team's book +
  // current YES/NO toggle and push it through the external-write path
  // (which also bumps priceFlashKey for the glow). Without this, a
  // trader who switches teams keeps the stale price typed into the
  // input — easy way to fire an order at the wrong number. Reads from
  // the panel's current venue so a Poly-side trader keeps seeing Poly
  // prices on team swap.
  const applyTeamToggle = useCallback(
    (next: TeamSide) => {
      setSelectedSide(next);
      onTeamSideChange?.(next);
      const venueBooks = panelVenue === 'kalshi' ? game.kalshi : game.polymarket;
      const nextBook = next === 'away' ? venueBooks?.away : venueBooks?.home;
      const hint = bestPriceCentsForOrderSide(nextBook ?? undefined, panelSide);
      if (hint !== undefined) {
        writeFormPriceFromExternal(String(hint));
      }
    },
    [game.kalshi, game.polymarket, panelVenue, panelSide, onTeamSideChange, writeFormPriceFromExternal],
  );

  // Venue swap — same idea as team swap: refresh the price hint from
  // the new venue's book on the currently-selected team + side, and
  // glow the input so the trader can't miss that the order is now
  // routing somewhere else.
  const applyVenueToggle = useCallback(
    (next: 'kalshi' | 'polymarket') => {
      setPanelVenue(next);
      const venueBooks = next === 'kalshi' ? game.kalshi : game.polymarket;
      const nextBook = selectedSide === 'away' ? venueBooks?.away : venueBooks?.home;
      const hint = bestPriceCentsForOrderSide(nextBook ?? undefined, panelSide);
      if (hint !== undefined) {
        writeFormPriceFromExternal(String(hint));
      }
    },
    [game.kalshi, game.polymarket, selectedSide, panelSide, writeFormPriceFromExternal],
  );

  // Venue availability — used to disable the tab for a venue that has
  // no book on this game at all (e.g. spread/total events on Poly).
  const kalshiAvailable = !!(game.kalshi?.away || game.kalshi?.home);
  const polyAvailable = !!(game.polymarket?.away || game.polymarket?.home);

  // Depend on the primitive fields, NOT the tradeIntent object reference.
  // Callers (especially GameDetail) rebuild a fresh tradeIntent literal
  // on every render via tradeCtxToTradeIntent, so a `[tradeIntent]` dep
  // would fire on every parent render and stomp the user's local
  // team-tab / venue-tab click. Primitives change only when the actual
  // value changes.
  const tradeIntentSide = tradeIntent?.side;
  const tradeIntentVenue = tradeIntent?.venue;
  const tradeIntentPrefillPrice = tradeIntent?.prefillPrice;
  const tradeIntentInitialOrderSide = tradeIntent?.initialOrderSide;
  const tradeIntentGameKey = tradeIntent?.gameKey;
  useEffect(() => {
    if (tradeIntentSide) setSelectedSide(tradeIntentSide);
    if (tradeIntentVenue) setPanelVenue(tradeIntentVenue);
    // Skip a stale prefill (tagged for a different game/line) so the price
    // seeds from the new book's best bid instead. See prefillMatchesGame.
    if (
      tradeIntentPrefillPrice != null &&
      (tradeIntentGameKey == null || tradeIntentGameKey === game.key)
    ) {
      writeFormPriceFromExternal(String(tradeIntentPrefillPrice));
    }
    if (tradeIntentInitialOrderSide) {
      setPanelSide(tradeIntentInitialOrderSide);
    }
  }, [
    game.key,
    tradeIntentSide,
    tradeIntentVenue,
    tradeIntentPrefillPrice,
    tradeIntentInitialOrderSide,
    tradeIntentGameKey,
    writeFormPriceFromExternal,
  ]);

  // Active book's ticker for the panel's current (venue × team). When
  // a spread/total chip is picked anywhere — the dashboard's chip
  // strip, the panel's own MiniLineStrip — the synth game gets a new
  // line ticker, so this primitive changes while the panel stays
  // mounted. Treat that transition as an external price write: bump
  // priceFlashKey (input glows + qty default fills if blank) and
  // write the best-side hint from the new book. Same UX as clicking
  // a pill — chip swap should land an order ready to submit.
  //
  // The reseed-vs-keep decision is delegated to
  // shouldReseedPriceOnBookChange — see its doc for why a team/venue
  // switch must NOT reseed (it would clobber a just-clicked ladder
  // level with top-of-book). The ref carries (ticker, side, venue) so
  // the predicate can tell a line swap from a selection switch.
  const activeBookForFormRef = useRef<ActiveBookSelection | null>(null);
  const activeBookForForm = panelVenue === 'kalshi'
    ? (selectedSide === 'away' ? game.kalshi?.away : game.kalshi?.home)
    : (selectedSide === 'away' ? game.polymarket?.away : game.polymarket?.home);
  const activeBookTicker = activeBookForForm?.marketTicker ?? null;
  useEffect(() => {
    const prev = activeBookForFormRef.current;
    const next = { ticker: activeBookTicker, side: selectedSide, venue: panelVenue };
    activeBookForFormRef.current =
      activeBookTicker === null
        ? null
        : { ticker: activeBookTicker, side: selectedSide, venue: panelVenue };
    if (!shouldReseedPriceOnBookChange(prev, next)) return;
    const hint = bestPriceCentsForOrderSide(activeBookForForm ?? undefined, panelSide);
    if (hint !== undefined) {
      writeFormPriceFromExternal(String(hint));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBookTicker]);

  // Ask Hydra to subscribe upstream to this game's Kalshi orderbooks while the
  // panel is open. No-op until Hydra serves /api/* over HTTPS — see
  // docs/hydra-server-asks.md.
  useHydraOrderbookWarmup([game.kalshi?.away.marketTicker, game.kalshi?.home.marketTicker]);

  // Register the panel's effective tickers via its own orderbook-needs
  // channel so depth follows the panel chip even when it has diverged
  // from the dashboard's selection. Deduped against `spreads-totals` and
  // `moneyline` channels by the store.
  const panelKalshiTicker = game.kalshi?.away?.marketTicker ?? null;
  const panelPolyTicker = game.polymarket?.away?.marketTicker ?? null;
  useEffect(() => {
    const tickers: string[] = [];
    if (panelKalshiTicker) tickers.push(panelKalshiTicker);
    if (panelPolyTicker) tickers.push(panelPolyTicker);
    useMarketStore.getState().setOrderbookNeeds('detail-panel', tickers);
    return () => {
      useMarketStore.getState().setOrderbookNeeds('detail-panel', []);
    };
  }, [panelKalshiTicker, panelPolyTicker]);

  const teamName = selectedSide === 'away' ? game.awayName : game.homeName;
  const kalshiBook = selectedSide === 'away' ? game.kalshi?.away : game.kalshi?.home;
  const polyBook = selectedSide === 'away' ? game.polymarket?.away : game.polymarket?.home;
  // Soccer 3-way rows ride the betLineContext panel mode, but unlike
  // spread/total synths their price strip is wanted (Joshua, Jun 12 call:
  // K/P missing on World Cup quick-order). The synth dupes one outcome
  // book into both slots, so the strip needs outcome/complement columns
  // (NO ladder on the right) instead of away/home — see the strip below.
  const isMultiSynth = gameMarketKind(game) === 'multi';
  // Side-panel book depth — user preference (Settings → Display). Default 8
  // keeps today's fan; Prof's tighter view is 3 per side.
  const bookDepthLevels = useDisplaySettings((st) => st.bookDepthLevels);

  // Drive bids/asks off panelSide, with the same phantom-level sanity filter
  // the inline ladder + Yes/No pills use so this panel matches them at the
  // spread.
  //   YES side: bids = book.yes (yes bids), asks = 100 − no bids
  //   NO  side: bids = book.no  (no bids),  asks = 100 − yes bids
  const kalshiSide = buildSide(kalshiBook, panelSide);
  const polySide = buildSide(polyBook, panelSide);

  return (
    <div className="bg-card/95 border-border flex h-full w-96 flex-col border-l">
      {/* Header — Spread/Total renders the bet-description title with a chip
        * strip in place of the matchup. The away/home toggle below is hidden
        * since both slots point at the same synthesized VenueBook. */}
      {betLineContext ? (
        <div className="border-border space-y-2 border-b px-4 py-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="text-foreground text-sm font-semibold leading-snug">
                {betLineContext.titleFor(panelSide, panelChipIdx)}
              </h3>
              <p className="text-muted-foreground mt-0.5 font-mono text-[11px]">
                {game.awayCode} @ {game.homeCode} &middot; {game.date}
              </p>
            </div>
            {onClose && (
              <button onClick={onClose} className="text-muted-foreground hover:bg-accent hover:text-foreground shrink-0 rounded p-1">
                <X size={16} />
              </button>
            )}
          </div>
          <MiniLineStrip
            chips={betLineContext.chips}
            selectedIdx={panelChipIdx}
            onSelect={handlePanelChipSelect}
          />
        </div>
      ) : (
        <>
          {/* Moneyline header */}
          <div className="border-border flex items-center justify-between border-b px-4 py-3">
            <div>
              <h3 className="text-foreground flex items-center gap-2 text-sm font-semibold">
                <span>
                  {game.awayName} @ {game.homeName}
                </span>
                {game.gameState === 'live' && <LiveBadge />}
              </h3>
              <p className="text-muted-foreground mt-0.5 text-xs">
                {game.date} &middot; {game.timePt} PT
              </p>
            </div>
            {onClose && (
              <button onClick={onClose} className="text-muted-foreground hover:bg-accent hover:text-foreground rounded p-1">
                <X size={16} />
              </button>
            )}
          </div>

          {/* Team selector — moneyline only. */}
          <div className="border-border flex border-b">
            <button
              className={cn(
                'flex-1 py-2 text-center text-sm font-medium transition-colors',
                selectedSide === 'away'
                  ? 'bg-primary/20 text-primary border-primary border-b-2'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              onClick={() => applyTeamToggle('away')}
            >
              {game.awayName}
            </button>
            <button
              className={cn(
                'flex-1 py-2 text-center text-sm font-medium transition-colors',
                selectedSide === 'home'
                  ? 'bg-primary/20 text-primary border-primary border-b-2'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              onClick={() => applyTeamToggle('home')}
            >
              {game.homeName}
            </button>
          </div>
        </>
      )}

      {/* Venue selector — the most prominent control on the panel.
          Mounted above OrderEntryForm so the trader can't miss which
          venue their order is routing to. Disabled side falls
          unclickable when the game has no book on that venue (e.g.
          spread/total on Poly today). Rendered in both moneyline and
          betLineContext layouts so the choice is always visible. */}
      <div className="border-border flex border-b">
        <button
          type="button"
          disabled={!kalshiAvailable}
          onClick={() => applyVenueToggle('kalshi')}
          className={cn(
            'flex-1 py-2 text-center text-xs font-bold uppercase tracking-wider transition-colors',
            panelVenue === 'kalshi'
              ? 'bg-blue-500/20 text-blue-300 border-blue-500 border-b-2'
              : !kalshiAvailable
                ? 'text-muted-foreground/30 cursor-not-allowed'
                : 'text-muted-foreground hover:text-foreground',
          )}
        >
          Kalshi
        </button>
        <button
          type="button"
          disabled={!polyAvailable}
          onClick={() => applyVenueToggle('polymarket')}
          className={cn(
            'flex-1 py-2 text-center text-xs font-bold uppercase tracking-wider transition-colors',
            panelVenue === 'polymarket'
              ? 'bg-purple-500/20 text-purple-300 border-purple-500 border-b-2'
              : !polyAvailable
                ? 'text-muted-foreground/30 cursor-not-allowed'
                : 'text-muted-foreground hover:text-foreground',
          )}
        >
          Polymarket
        </button>
      </div>

      {/* Order Entry */}
      <OrderEntryForm
        game={game}
        selectedSide={selectedSide}
        venue={panelVenue}
        orderSide={panelSide}
        onOrderSideChange={setPanelSide}
        price={formPrice}
        onPriceChange={setFormPrice}
        priceFlashKey={priceFlashKey}
        orderType={formOrderType}
        onOrderTypeChange={setFormOrderType}
      />

      {/* Open orders for THIS game — cancel inline without going to the
          positions tab (StreamRift trading-safety ask #2/#3). */}
      <GameOpenOrders game={game} />

      {/* Per-team price summary \u2014 moneyline only; for synthetic spread/total
        * GameRows the away/home columns would show identical numbers.
        * U (Unabated consensus) row only renders when the caller supplies
        * consensus values \u2014 GameDetail wires it through useOddsStream;
        * the value dashboard caller passes nothing so the row's hidden
        * there. YES/NO toggle (panelSide) flips the consensus the same
        * way K and P flip: NO = 100 \u2212 fairProb. */}
      {(!betLineContext || isMultiSynth) && (
        <div className="border-border border-b px-4 py-2">
          <div className="grid grid-cols-3 gap-2">
            <div />
            <div className="text-muted-foreground text-center text-[10px] font-semibold uppercase">{isMultiSynth ? game.awayName : game.awayCode}</div>
            <div className="text-muted-foreground text-center text-[10px] font-semibold uppercase">{isMultiSynth ? game.homeName : game.homeCode}</div>
          </div>
          {/* U row stays match-level away/home — wrong frame for an
              outcome/complement pair (nonsensical for Draw), so multi
              hides it until a per-outcome consensus lands. */}
          {!isMultiSynth && (unabated?.awayCents != null || unabated?.homeCents != null) && (
            <div className="grid grid-cols-3 gap-2">
              <div
                className="text-xs font-semibold text-amber-400"
                title="Unabated consensus fair-probability (full game)"
              >
                U
              </div>
              <div className="text-foreground/80 text-center font-mono text-xs">
                {formatConsensusCents(unabated.awayCents)}
              </div>
              <div className="text-foreground/80 text-center font-mono text-xs">
                {formatConsensusCents(unabated.homeCents)}
              </div>
            </div>
          )}
          {/* K and P rows render YES prices regardless of the panel's
              YES/NO toggle. The whole strip is meant to read like
              "each venue's view of each team's win probability" — all
              three rows directional same way, so a glance at the
              column tells you who's favored across all three sources.
              Active-side prices (the ones the trader is about to pay)
              live in the orderbook below + the trade ticket above. */}
          <div className="grid grid-cols-3 gap-2">
            <div className="text-xs font-semibold text-blue-400">K</div>
            <div className="text-foreground/80 text-center font-mono text-xs">
              {formatSidePriceCents(game.kalshi?.away, 'yes')}
            </div>
            <div className="text-foreground/80 text-center font-mono text-xs">
              {formatSidePriceCents(game.kalshi?.home, isMultiSynth ? 'no' : 'yes')}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="text-xs font-semibold text-purple-400">P</div>
            <div className="text-foreground/80 text-center font-mono text-xs">
              {formatSidePriceCents(game.polymarket?.away, 'yes')}
            </div>
            <div className="text-foreground/80 text-center font-mono text-xs">
              {formatSidePriceCents(game.polymarket?.home, isMultiSynth ? 'no' : 'yes')}
            </div>
          </div>
        </div>
      )}

      {/* Orderbooks. Clicking any level routes the ticket to the
          venue that was clicked + writes the price (and order type
          where applicable). Lets the trader pick a target by tapping
          the depth instead of manually toggling the venue tab. */}
      <div className="flex-1 space-y-3 overflow-y-auto p-3">
        {kalshiBook && (
          <ExchangeOrderBook
            venue="kalshi"
            maxLevels={bookDepthLevels}
            teamLabel={`${betLineContext ? '' : `${teamName} — `}${panelSide === 'yes' ? 'YES' : 'NO'}`}
            bids={kalshiSide.bids}
            asks={kalshiSide.asks}
            onLevelClick={(info) => {
              // Switch the form to Kalshi first — the trader may have
              // been on Poly and clicked into K's depth. Bid click →
              // resting limit at that price. Ask click → take-the-
              // offer market order (Kalshi relay supports it).
              setPanelVenue('kalshi');
              if (info.side === 'bid') {
                setFormOrderType('limit');
                writeFormPriceFromExternal(String(info.priceCents));
              } else {
                setFormOrderType('market');
              }
            }}
          />
        )}
        {polyBook && (
          <ExchangeOrderBook
            venue="polymarket"
            maxLevels={bookDepthLevels}
            teamLabel={`${betLineContext ? '' : `${teamName} — `}${panelSide === 'yes' ? 'YES' : 'NO'}`}
            bids={polySide.bids}
            asks={polySide.asks}
            onLevelClick={(info) => {
              // Route the ticket to Polymarket. Poly's relay doesn't
              // support market orders today, so an ask click becomes a
              // takeable limit at the ask price rather than a market
              // type — same final fill behavior, just a different
              // order shape the relay accepts.
              setPanelVenue('polymarket');
              setFormOrderType('limit');
              writeFormPriceFromExternal(String(info.priceCents));
            }}
          />
        )}
      </div>
    </div>
  );
}
